import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, OnDestroy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TooltipDirective } from 'ngx-bootstrap/tooltip';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import {
  cleanRuleDescription,
  CreateCustomWafRuleRequest,
  CustomWafRule,
  SHIELD_PATHS,
  UpdateCustomWafRuleRequest,
  WafMappedEnumList,
  WafRecommendedOperator,
  WafRuleActionType,
  WafRuleOperatorType,
  WafRuleTransformationType,
  WafSupportedValues,
} from '@bn/app-shield';
import { AppRoutes } from '@bn/dashboard-core/enums';
import { DashboardSharedModule } from '@bn/dashboard-shared';
import { ContentService } from '@bn/shared/services';
import { StoreRequest } from '@bn/shared/state-store';
import { RegexUtils } from '@bn/shared/utils';
import { MarkAllTouchedDirective } from '@bn/web-ui';
import { ButtonComponent } from '@bn/web-ui/button/button.component';
import { CardComponent } from '@bn/web-ui/card/card.component';
import { InputComponent } from '@bn/web-ui/input/input.component';
import { LoadingComponent } from '@bn/web-ui/loading/loading.component';
import { SelectOptions } from '@bn/web-ui/select/models';
import { SelectComponent } from '@bn/web-ui/select/select.component';
import { FormcontrolErrorsComponent } from '@bn/web-ui/standalone/formcontrol-errors/formcontrol-errors.component';
import { MapWafEnumToSelectItems, MapWafValuesToSelectItems } from '../../pipes';
import { ShieldApiStore } from '../../stores';

@Component({
  templateUrl: './app-shield-waf-custom-rule-add.page.html',
  styleUrls: ['./app-shield-waf-custom-rule-add.page.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DashboardSharedModule,
    MapWafEnumToSelectItems,
    MapWafValuesToSelectItems,
    ButtonComponent,
    CardComponent,
    FormcontrolErrorsComponent,
    InputComponent,
    LoadingComponent,
    SelectComponent,
    FaIconComponent,
    NgxSkeletonLoaderComponent,
    TooltipDirective,
    MarkAllTouchedDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppShieldWafCustomRuleAddPage implements OnDestroy {
  #contentService = inject(ContentService);
  #draftRule: Partial<CreateCustomWafRuleRequest | CustomWafRule> | undefined;
  #fb = inject(FormBuilder);
  #route = inject(ActivatedRoute);
  #router = inject(Router);
  #routeParams = toSignal(this.#route.params);
  #shieldStore = inject(ShieldApiStore);

  isAddFinished = this.#shieldStore.isFinished(StoreRequest.Add, ['wafCustomRules']);
  isUpdateFinished = this.#shieldStore.isFinished(StoreRequest.Update, ['wafCustomRules']);
  isAddLoading = this.#shieldStore.isLoading(StoreRequest.Add, ['wafCustomRules']);
  isUpdateLoading = this.#shieldStore.isLoading(StoreRequest.Update, ['wafCustomRules']);
  isSaveLoading = this.#shieldStore.isLoading(StoreRequest.Add, ['wafCustomRules']);
  isPremium = true; // show all as regular
  wafRuleId = 0;
  wafRuleVariableTypes?: WafMappedEnumList;
  wafRuleOperatorType?: WafMappedEnumList;
  wafRuleActionType?: WafMappedEnumList;
  wafRuleTransformationType?: WafMappedEnumList;

  options: Partial<SelectOptions> = {
    clearable: false,
    closeOnSelect: false,
    dropdownWidth: '100',
    hideClear: true,
    hideName: false,
    hideRequiredLabel: true,
    isSearchable: false,
    minWidth: '100px',
    multiple: true,
    showOnlyCount: false,
  };

  requestMatchers = this.#fb.array([this.createMatcherForm()]);

  form = this.#fb.group({
    actionType: this.#fb.control<WafRuleActionType | null>(null, [Validators.required]),
    description: this.#fb.control<string>('', [Validators.pattern(RegexUtils.LettersAndNumbersRegex), Validators.maxLength(500)]),
    name: this.#fb.control<string>('', [Validators.required, Validators.pattern(RegexUtils.LettersAndNumbersRegex), Validators.maxLength(100)]),
    requestMatchers: this.requestMatchers,
    transformations: this.#fb.control<WafRuleTransformationType[]>([]),
  });

  // make signals out of observables, to reduce code, add debounceTime if needed if signal goes crazy
  #requestMatchersValue = toSignal(this.requestMatchers.valueChanges);
  #previousValues = this.#requestMatchersValue();

  constructor() {
    const pullZoneId = this.#shieldStore.pullZoneId();

    this.#contentService.setTitle('Add custom WAF rule');
    this.#contentService.setBackButton({
      routerLink: `/${AppRoutes.CDN}/${pullZoneId}/${AppRoutes.Shield}/${SHIELD_PATHS.ShieldWaf}`,
    });

    this.setWafEnums();
    this.listenToStoreSignals();
    this.listenToRouteParams();
    this.listenToRequestMatchersChanges();
  }

  ngOnDestroy(): void {
    this.#contentService.setBackButton(null);
  }

  canDeactivate(): boolean {
    return !this.form.dirty;
  }

  public patchWafRuleForm(): void {
    if (!this.#draftRule) return;

    const ruleSettings = this.#draftRule.ruleConfiguration;
    const [variableKey, variableValue] = Object.entries(ruleSettings?.variableTypes ?? {})?.[0] ?? {};
    const conditionsVariable = this.wafRuleVariableTypes?.enumValues?.find(({ name }) => name === variableKey)?.value;

    // set main matcher, form already exists
    this.requestMatchers.at(0).patchValue({
      ConditionsVariable: conditionsVariable ?? null,
      ConditionsOperator: ruleSettings?.operatorType ?? null,
      VariableValue: variableValue ?? null,
      OperatorValue: ruleSettings?.value ?? null,
    });

    // set chained matchers, the same way as main one, but append new forms
    ruleSettings?.chainedRuleConditions?.forEach((chainMatcher) => {
      const [variableKey, variableValue] = Object.entries(chainMatcher?.variableTypes ?? {})?.[0] ?? {};
      const conditionsVariable = this.wafRuleVariableTypes?.enumValues?.find(({ name }) => name === variableKey)?.value;

      this.onAddMatcher({
        ConditionsVariable: conditionsVariable ?? null,
        ConditionsOperator: chainMatcher.operatorType ?? null,
        VariableValue: variableValue ?? null,
        OperatorValue: chainMatcher.value ?? null,
      });
    });

    // top level form update
    this.form.patchValue(
      {
        actionType: ruleSettings?.actionType,
        name: this.#draftRule.ruleName,
        description: this.#draftRule.ruleDescription,
        transformations: ruleSettings?.transformationTypes,
      },
      { emitEvent: false }, // no emit no form dirty!
    );

    // Set a whole form at once, so we get default state
    this.form.reset(this.form.getRawValue());
  }

  onCancelClick(): void {
    if (this.wafRuleId) this.#router.navigate(['../../'], { relativeTo: this.#route });
    else this.#router.navigate(['../'], { relativeTo: this.#route });
  }

  onRemoveMatcher(index: number): void {
    this.requestMatchers.removeAt(index);
    this.requestMatchers.markAsDirty();
    this.requestMatchers.updateValueAndValidity();
    this.#previousValues = this.requestMatchers.getRawValue();
  }

  onAddMatcher(values?: MatcherFormValue): void {
    this.requestMatchers.push(this.createMatcherForm(values));
  }

  createMatcherForm(values?: MatcherFormValue): FormGroup {
    return this.#fb.group({
      ConditionsVariable: this.#fb.control<number | null>(values?.ConditionsVariable ?? null, [Validators.required]),
      ConditionsOperator: this.#fb.control<WafRuleOperatorType | null>(values?.ConditionsOperator ?? null, [Validators.required]),
      VariableValue: this.#fb.control<string | null>(values?.VariableValue ?? null),
      OperatorValue: this.#fb.control<string | null>(values?.OperatorValue ?? null, [Validators.required]),
    });
  }

  // Predefined, selectable Variable Value list for the selected variable (empty/typed allowed when editable).
  getSupportedValues(conditionsVariable: number | null | undefined): WafSupportedValues | undefined {
    if (conditionsVariable == null) return undefined;

    return this.wafRuleVariableTypes?.enumValues?.find((en) => en.value === conditionsVariable)?.supportedValues ?? undefined;
  }

  // Recommended operator + predefined Operator Value list for the selected variable.
  getRecommendedOperator(conditionsVariable: number | null | undefined): WafRecommendedOperator | undefined {
    if (conditionsVariable == null) return undefined;

    return this.wafRuleVariableTypes?.enumValues?.find((en) => en.value === conditionsVariable)?.recommendedOperator ?? undefined;
  }

  onPaste(event: ClipboardEvent, formName: string): void {
    event.preventDefault();

    const pastedData = event.clipboardData?.getData('text') || '';
    const cleanedData = cleanRuleDescription(pastedData);

    // Update the input value
    this.form.get(formName)?.patchValue(cleanedData);
  }

  onSubmitForm(): void {
    if (!this.form.valid) return;

    const formValues = this.form.getRawValue();
    const matchers = formValues.requestMatchers;
    const mainMatcher = matchers[0];
    const chainedRuleConditions = matchers.slice(1);
    const mainVariableType =
      this.wafRuleVariableTypes?.enumValues?.find(({ value }) => mainMatcher.ConditionsVariable !== null && value === Number(mainMatcher['ConditionsVariable']))
        ?.name ?? '';

    this.#draftRule = {
      shieldZoneId: Number(this.#shieldStore.selectedEntityId()()),
      ruleName: formValues.name,
      ruleDescription: formValues.description ?? undefined,
      ruleConfiguration: {
        actionType: formValues.actionType ?? undefined,
        variableTypes: {
          [mainVariableType]: mainMatcher.VariableValue ?? '',
        },
        operatorType: mainMatcher.ConditionsOperator ?? undefined,
        transformationTypes: formValues.transformations ?? [],
        value: mainMatcher.OperatorValue ?? '',
        chainedRuleConditions:
          chainedRuleConditions?.length > 0
            ? chainedRuleConditions.map((matcher) => {
                const variableType =
                  this.wafRuleVariableTypes?.enumValues?.find(
                    ({ value }) => matcher.ConditionsVariable !== null && value === Number(matcher['ConditionsVariable']),
                  )?.name ?? '';

                return {
                  variableTypes: {
                    [variableType]: matcher['VariableValue'] ?? '',
                  },
                  operatorType: matcher['ConditionsOperator'] ?? undefined,
                  value: matcher['OperatorValue'],
                };
              })
            : null,
      },
    };

    const { shieldZoneId, ...data } = this.#draftRule ?? {};
    if (this.isUpdateFinished() || this.isAddFinished() || !shieldZoneId) return;

    this.#draftRule = undefined;

    if (this.wafRuleId) {
      this.#shieldStore.updateWafRule({
        id: this.wafRuleId,
        data: data as UpdateCustomWafRuleRequest,
        customFields: ['wafCustomRules'],
      });
    } else {
      this.#shieldStore.createWafRule({ data: data as CreateCustomWafRuleRequest });
    }

    // default with last form values, so it won't nag for leaving dirty site
    this.form.reset(this.form.getRawValue());
  }

  private listenToRouteParams(): void {
    effect(() => {
      this.wafRuleId = Number(this.#routeParams()?.customWafRuleId || 0);

      if (this.wafRuleId) {
        this.#contentService.setTitle('Edit custom WAF rule');
        this.#draftRule = this.#shieldStore.wafCustomRules().find((rule) => rule.id === this.wafRuleId);

        if (this.#draftRule) {
          this.patchWafRuleForm();
        }
      }
    });
  }

  private listenToRequestMatchersChanges(): void {
    effect(() => {
      const matchers = this.#requestMatchersValue();
      if (!matchers) return;

      matchers.forEach((_matcher, index) => {
        const matcherForm = this.requestMatchers.at(index);
        const variableValueControl = matcherForm.get('VariableValue');
        const operatorControl = matcherForm.get('ConditionsOperator');
        const operatorValueControl = matcherForm.get('OperatorValue');
        const conditionsVariable = matcherForm.get('ConditionsVariable')?.value;
        const variableEnum = this.wafRuleVariableTypes?.enumValues?.find((en) => en.value === conditionsVariable);
        const recommendedOperator = variableEnum?.recommendedOperator;

        const previous = this.#previousValues?.[index];
        const isInit = !previous;
        const hasConditionsVariableChanged = !isInit && previous.ConditionsVariable !== conditionsVariable;

        if (recommendedOperator?.operator != null && (hasConditionsVariableChanged || (isInit && operatorControl?.value == null))) {
          operatorControl?.setValue(recommendedOperator.operator, { emitEvent: false });
        }

        // VariableValue hiding if optional
        if (variableValueControl && (hasConditionsVariableChanged || isInit)) {
          // Reset value on change, but keep it at init (so an existing rule's value is preserved)
          if (hasConditionsVariableChanged) variableValueControl.setValue(null, { emitEvent: false });
          this.#setControlEnabled(variableValueControl, variableEnum?.optionalInput !== false);
        }

        const conditionsOperator = operatorControl?.value;
        const operatorEnum = this.wafRuleOperatorType?.enumValues?.find((en) => en.value === conditionsOperator);
        const hasConditionsOperatorChanged = !isInit && previous.ConditionsOperator !== conditionsOperator;

        if (operatorValueControl && (hasConditionsOperatorChanged || isInit)) {
          if (hasConditionsOperatorChanged) operatorValueControl.setValue(null, { emitEvent: false });
          this.#setControlEnabled(operatorValueControl, operatorEnum?.optionalInput !== false);
        }
      });

      this.#previousValues = this.requestMatchers.getRawValue();
    });
  }

  #setControlEnabled(control: AbstractControl, enabled: boolean): void {
    if (enabled) {
      control.enable({ emitEvent: false });
    } else {
      control.disable({ emitEvent: false });
    }
  }

  private listenToStoreSignals(): void {
    effect(() => {
      if (this.isAddFinished()) {
        this.#router.navigate(['../'], { relativeTo: this.#route });
      }
      if (this.isUpdateFinished()) {
        this.#router.navigate(['../../'], { relativeTo: this.#route });
      }
    });
  }

  private setWafEnums(): void {
    effect(() => {
      const wafEnums = this.#shieldStore.wafEnums();

      this.wafRuleVariableTypes = wafEnums?.find((apiEnum) => apiEnum.enumName === 'WafRuleVariableType') ?? <WafMappedEnumList>[];

      this.wafRuleOperatorType = wafEnums?.find((apiEnum) => apiEnum.enumName === 'WafRuleOperatorType') ?? <WafMappedEnumList>[];

      this.wafRuleActionType = wafEnums?.find((apiEnum) => apiEnum.enumName === 'WafRuleActionType') ?? <WafMappedEnumList>[];

      this.wafRuleTransformationType = wafEnums?.find((apiEnum) => apiEnum.enumName === 'WafRuleTransformationType') ?? <WafMappedEnumList>[];
    });
  }
}

type MatcherFormValue = {
  ConditionsVariable: number | null;
  ConditionsOperator: WafRuleOperatorType | null;
  VariableValue: string | null;
  OperatorValue: string | null;
};
